import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { servicesAPI, timeSlotsAPI, bookingsAPI } from '../utils/api';
import CustomerSearch from '../components/CustomerSearch';
import FiveDayCalendar from '../components/FiveDayCalendar';
import dayjs from 'dayjs';

function AdminBookingCreate() {
  const navigate = useNavigate();
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [notes, setNotes] = useState('');
  const [useSavedCard, setUseSavedCard] = useState(false);
  const [hasSavedCard, setHasSavedCard] = useState(false);
  const [loading, setLoading] = useState(false);

  // Recurring booking state
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringFrequency, setRecurringFrequency] = useState('weekly'); // weekly, biweekly, monthly
  const [recurringCount, setRecurringCount] = useState(4); // Number of occurrences

  // Calendar state
  const [calendarStartDate, setCalendarStartDate] = useState(() => dayjs().format('YYYY-MM-DD'));

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    if (selectedDate && selectedService) {
      fetchAvailableSlots();
    }
  }, [selectedDate, selectedService]);

  useEffect(() => {
    // Check if customer has saved card
    if (selectedCustomer) {
      checkForSavedCard();
    }
  }, [selectedCustomer]);

  const fetchServices = async () => {
    try {
      const response = await servicesAPI.getAll();
      setServices(response.data.filter(s => s.active));
    } catch (error) {
      toast.error('Failed to load services');
    }
  };

  const fetchAvailableSlots = async () => {
    try {
      setLoading(true);
      const response = await timeSlotsAPI.getAvailable(selectedDate, selectedService.duration);
      setAvailableSlots(response.data);
    } catch (error) {
      toast.error('Failed to load available slots');
    } finally {
      setLoading(false);
    }
  };

  const checkForSavedCard = async () => {
    try {
      const response = await bookingsAPI.checkCard(selectedCustomer.email);
      setHasSavedCard(response.data.has_saved_card);
      setUseSavedCard(response.data.has_saved_card);
    } catch (error) {
      console.error('Error checking for saved card:', error);
      setHasSavedCard(false);
      setUseSavedCard(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedCustomer) {
      toast.error('Please select a customer');
      return;
    }

    if (!selectedService) {
      toast.error('Please select a service');
      return;
    }

    if (!selectedSlot) {
      toast.error('Please select a time slot');
      return;
    }

    setLoading(true);
    try {
      if (isRecurring && recurringDates.length > 1) {
        // Create recurring bookings
        await bookingsAPI.adminCreateRecurring({
          customer_id: selectedCustomer.id,
          service_id: selectedService.id,
          dates: recurringDates.map(d => d.date),
          start_time: selectedSlot.start_time,
          notes,
          use_saved_card: useSavedCard,
          frequency: recurringFrequency
        });

        toast.success(`${recurringDates.length} recurring bookings created successfully!`);
      } else {
        // Create single booking
        await bookingsAPI.adminCreate({
          customer_id: selectedCustomer.id,
          service_id: selectedService.id,
          date: selectedDate,
          start_time: selectedSlot.start_time,
          notes,
          use_saved_card: useSavedCard
        });

        toast.success('Booking created successfully!');
      }
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Calculate recurring dates
  const recurringDates = useMemo(() => {
    if (!isRecurring || !selectedDate) return [];

    const dates = [];
    let currentDate = dayjs(selectedDate);

    for (let i = 0; i < recurringCount; i++) {
      dates.push({
        date: currentDate.format('YYYY-MM-DD'),
        display: currentDate.format('ddd, D MMM YYYY'),
        isFirst: i === 0,
      });

      // Move to next occurrence based on frequency
      if (recurringFrequency === 'weekly') {
        currentDate = currentDate.add(1, 'week');
      } else if (recurringFrequency === 'biweekly') {
        currentDate = currentDate.add(2, 'week');
      } else if (recurringFrequency === 'monthly') {
        currentDate = currentDate.add(1, 'month');
      }
    }

    return dates;
  }, [isRecurring, selectedDate, recurringFrequency, recurringCount]);

  return (
    <div className="container">
      <div className="card">
        <div className="card-header">
          <h2>Create Booking for Customer</h2>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Customer Selection */}
          <div className="form-group">
            <label>Customer *</label>
            <CustomerSearch
              onSelect={setSelectedCustomer}
              selectedCustomer={selectedCustomer}
            />
            {selectedCustomer && (
              <div style={{
                marginTop: '0.5rem',
                padding: '0.75rem',
                background: '#FAF8F5',
                borderRadius: '6px',
                border: '1px solid #E8E4E0'
              }}>
                <strong>{selectedCustomer.name}</strong>
                <div style={{ fontSize: '0.9rem', color: '#6B6B6B', marginTop: '0.25rem' }}>
                  {selectedCustomer.email} • {selectedCustomer.phone}
                </div>
              </div>
            )}
          </div>

          {/* Service Selection */}
          <div className="form-group">
            <label>Service *</label>
            <select
              className="form-control"
              value={selectedService?.id || ''}
              onChange={(e) => {
                const service = services.find(s => s.id === parseInt(e.target.value));
                setSelectedService(service);
                setSelectedSlot(null);
              }}
              required
            >
              <option value="">Select a service</option>
              {services.map((service) => (
                <option key={service.id} value={service.id}>
                  {service.name} - £{service.price} ({service.duration} min)
                </option>
              ))}
            </select>
          </div>

          {/* Date Selection - 5-Day Calendar */}
          <div className="form-group">
            <label>Date *</label>
            <div style={{
              border: '1px solid #E8E4E0',
              borderRadius: '8px',
              padding: '1rem',
              background: '#fff',
              marginTop: '0.5rem'
            }}>
              <FiveDayCalendar
                selectedDate={selectedDate}
                onDateSelect={(date) => {
                  setSelectedDate(date);
                  setSelectedSlot(null);
                }}
                startDate={calendarStartDate}
                onNavigate={(days) => setCalendarStartDate(prev => dayjs(prev).add(days, 'day').format('YYYY-MM-DD'))}
              />
            </div>
            {selectedDate && (
              <div style={{
                marginTop: '0.75rem',
                padding: '0.75rem 1rem',
                background: '#FAF8F5',
                borderRadius: '6px',
                borderLeft: '3px solid #8B2635',
                fontWeight: 500,
                color: '#8B2635'
              }}>
                Selected: {dayjs(selectedDate).format('dddd, D MMMM YYYY')}
              </div>
            )}
          </div>

          {/* Time Slot Selection */}
          {selectedDate && selectedService && (
            <>
              <div className="form-group">
                <label>Time Slot *</label>
                {loading ? (
                  <div style={{ padding: '1rem', textAlign: 'center', color: '#6B6B6B' }}>
                    Loading available slots...
                  </div>
                ) : availableSlots.length === 0 ? (
                  <div className="alert alert-info">
                    No available slots for this date. Please choose another date.
                  </div>
                ) : (
                  <div className="time-slots-grid">
                    {availableSlots.map((slot, index) => (
                      <div
                        key={`${slot.start_time}-${index}`}
                        className={`time-slot ${selectedSlot?.start_time === slot.start_time ? 'selected' : ''}`}
                        onClick={() => setSelectedSlot(slot)}
                      >
                        <div>{slot.start_time}</div>
                        <small style={{ fontSize: '0.85rem', opacity: 0.8 }}>to {slot.end_time}</small>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Notes */}
          <div className="form-group">
            <label>Notes (optional)</label>
            <textarea
              className="form-control"
              rows="3"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this booking..."
            />
          </div>

          {/* Recurring Booking Options */}
          {selectedSlot && (
            <div style={{
              marginTop: '1.5rem',
              padding: '1rem',
              background: '#F5F2EF',
              borderRadius: '8px',
              border: '1px solid #E8E4E0'
            }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: isRecurring ? '1rem' : 0 }}>
                <input
                  type="checkbox"
                  checked={isRecurring}
                  onChange={(e) => setIsRecurring(e.target.checked)}
                  style={{ marginRight: '0.5rem' }}
                />
                <span style={{ color: '#8B2635', fontWeight: 600 }}>
                  🔄 Make this a recurring appointment
                </span>
              </label>

              {isRecurring && (
                <div style={{ marginTop: '0.5rem' }}>
                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                    <div className="form-group" style={{ margin: 0, flex: 1, minWidth: '150px' }}>
                      <label style={{ fontSize: '0.85rem', color: '#8B2635' }}>Frequency</label>
                      <select
                        className="form-control"
                        value={recurringFrequency}
                        onChange={(e) => setRecurringFrequency(e.target.value)}
                      >
                        <option value="weekly">Weekly</option>
                        <option value="biweekly">Every 2 Weeks</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                    <div className="form-group" style={{ margin: 0, flex: 1, minWidth: '150px' }}>
                      <label style={{ fontSize: '0.85rem', color: '#8B2635' }}>Number of Bookings</label>
                      <select
                        className="form-control"
                        value={recurringCount}
                        onChange={(e) => setRecurringCount(parseInt(e.target.value))}
                      >
                        {[2, 3, 4, 5, 6, 8, 10, 12].map(n => (
                          <option key={n} value={n}>{n} appointments</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Preview of recurring dates */}
                  {recurringDates.length > 0 && (
                    <div style={{
                      background: '#FAF8F5',
                      borderRadius: '6px',
                      padding: '0.75rem 1rem',
                      maxHeight: '200px',
                      overflowY: 'auto'
                    }}>
                      <div style={{ fontWeight: 600, color: '#8B2635', marginBottom: '0.5rem', fontSize: '0.85rem' }}>
                        📅 Scheduled Dates ({recurringDates.length} appointments)
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.5rem' }}>
                        {recurringDates.map((d, idx) => (
                          <div
                            key={d.date}
                            style={{
                              padding: '0.5rem 0.75rem',
                              background: d.isFirst ? '#8B2635' : 'white',
                              color: d.isFirst ? 'white' : '#2C2C2C',
                              borderRadius: '4px',
                              fontSize: '0.85rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem'
                            }}
                          >
                            <span style={{ fontWeight: 600, minWidth: '20px' }}>{idx + 1}.</span>
                            <span>{d.display}</span>
                            {d.isFirst && <span style={{ marginLeft: 'auto', fontSize: '0.75rem' }}>First</span>}
                          </div>
                        ))}
                      </div>
                      <div style={{ marginTop: '0.75rem', fontSize: '0.8rem', color: '#6B6B6B' }}>
                        ⓘ All appointments will be at {selectedSlot.start_time}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Saved Card Option */}
          {hasSavedCard && (
            <div style={{
              marginTop: '1rem',
              padding: '1rem',
              background: '#F0F7F1',
              borderRadius: '8px',
              border: '1px solid #A8C9AD'
            }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={useSavedCard}
                  onChange={(e) => setUseSavedCard(e.target.checked)}
                  style={{ marginRight: '0.5rem' }}
                />
                <span style={{ color: '#4A7C59' }}>
                  Use customer's saved card for no-show protection
                </span>
              </label>
            </div>
          )}

          {/* Summary */}
          {selectedCustomer && selectedService && selectedSlot && (
            <div style={{
              marginTop: '1.5rem',
              padding: '1rem',
              background: '#FAF8F5',
              borderRadius: '8px',
              border: '1px solid #E8E4E0'
            }}>
              <h4 style={{ margin: '0 0 0.5rem 0' }}>Booking Summary</h4>
              <p style={{ margin: '0.25rem 0' }}><strong>Customer:</strong> {selectedCustomer.name}</p>
              <p style={{ margin: '0.25rem 0' }}><strong>Service:</strong> {selectedService.name}</p>
              {isRecurring && recurringDates.length > 1 ? (
                <>
                  <p style={{ margin: '0.25rem 0' }}>
                    <strong>Recurring:</strong> {recurringDates.length} appointments ({recurringFrequency === 'weekly' ? 'weekly' : recurringFrequency === 'biweekly' ? 'every 2 weeks' : 'monthly'})
                  </p>
                  <p style={{ margin: '0.25rem 0' }}><strong>First date:</strong> {dayjs(selectedDate).format('D MMM YYYY')}</p>
                  <p style={{ margin: '0.25rem 0' }}><strong>Last date:</strong> {dayjs(recurringDates[recurringDates.length - 1]?.date).format('D MMM YYYY')}</p>
                </>
              ) : (
                <p style={{ margin: '0.25rem 0' }}><strong>Date:</strong> {selectedDate}</p>
              )}
              <p style={{ margin: '0.25rem 0' }}><strong>Time:</strong> {selectedSlot.start_time}</p>
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '1.25rem', color: '#2C2C2C' }}>
                <strong>
                  Total: £{isRecurring && recurringDates.length > 1
                    ? (parseFloat(selectedService.price) * recurringDates.length).toFixed(2)
                    : selectedService.price}
                </strong>
                {isRecurring && recurringDates.length > 1 && (
                  <span style={{ fontSize: '0.85rem', color: '#6B6B6B', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                    (£{selectedService.price} × {recurringDates.length})
                  </span>
                )}
              </p>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !selectedCustomer || !selectedService || !selectedSlot}
            >
              {loading
                ? 'Creating...'
                : isRecurring && recurringDates.length > 1
                  ? `Create ${recurringDates.length} Bookings`
                  : 'Create Booking'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/admin/dashboard')}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AdminBookingCreate;
